import { useState, useEffect } from 'react';
import { X, Zap } from 'lucide-react';
import { labApi } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { CONTENT_SECTIONS } from './constants';

interface BatchGenerateModalProps {
  productId: string;
  onClose: () => void;
  onStarted: () => void;
}

const STYLE_PRESETS = [
  { id: 'social_proof', label: 'Social Proof', desc: 'Opinie, statystyki, case studies' },
  { id: 'problem_solution', label: 'Problem → Rozwiązanie', desc: 'Ból klienta → produkt jako rozwiązanie' },
  { id: 'lifestyle', label: 'Lifestyle', desc: 'Aspiracyjny, wymarzone życie' },
  { id: 'fear_urgency', label: 'Fear / Urgency', desc: 'FOMO, deadline, konsekwencje' },
  { id: 'testimonial', label: 'Testimonial', desc: 'Pierwsza osoba, autentyczna opinia' },
];

export function BatchGenerateModal({ productId, onClose, onStarted }: BatchGenerateModalProps) {
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [variantCount, setVariantCount] = useState(1);
  const [selectedPresets, setSelectedPresets] = useState<string[]>([]);
  const [videoStrategies, setVideoStrategies] = useState<Array<{ key: string; label: string }>>([]);
  const [selectedStrategies, setSelectedStrategies] = useState<string[]>(['EXPLAINER']);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const videoSelected = selectedTypes.includes('VIDEO_SCRIPT');

  useEffect(() => {
    if (!videoSelected || videoStrategies.length > 0) return;
    labApi.getVideoStrategies()
      .then(res => setVideoStrategies(res.data.data?.strategies || []))
      .catch(err => console.error('Failed to load video strategies:', err));
  }, [videoSelected, videoStrategies.length]);

  const toggleType = (type: string) => {
    setSelectedTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  const togglePreset = (id: string) => {
    setSelectedPresets(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
  };

  const toggleStrategy = (key: string) => {
    setSelectedStrategies(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const perTypeMultiplier = (type: string) => (type === 'VIDEO_SCRIPT' ? Math.max(selectedStrategies.length, 1) : 1);
  const totalJobs = selectedTypes.reduce((sum, type) => sum + perTypeMultiplier(type), 0) * variantCount * Math.max(selectedPresets.length, 1);

  const handleStart = async () => {
    if (selectedTypes.length === 0) return;
    if (videoSelected && selectedStrategies.length === 0) return;
    setSubmitting(true);
    try {
      await labApi.startBatch(productId, {
        contentTypes: selectedTypes,
        variantCount,
        stylePresets: selectedPresets,
        videoStrategies: videoSelected ? selectedStrategies : undefined,
      });
      onStarted();
    } catch (err) {
      console.error('Batch start failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-surface-raised rounded-2xl border border-border w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-text-primary flex items-center gap-2">
            <Zap className="w-5 h-5 text-accent-primary" />
            Batch Generate
          </h2>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Content types */}
          <div>
            <label className="text-xs font-medium text-text-secondary mb-2 block">Typy treści</label>
            <div className="grid grid-cols-2 gap-2">
              {CONTENT_SECTIONS.map(section => {
                const Icon = section.icon;
                const isSelected = selectedTypes.includes(section.type);
                return (
                  <button
                    key={section.type}
                    onClick={() => toggleType(section.type)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs border transition-all ${
                      isSelected
                        ? `${section.bgClass} ${section.colorClass} ${section.borderClass}`
                        : 'text-text-secondary border-border hover:border-border-strong'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {section.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Strategie wideo (jeden skrypt per strategia) */}
          {videoSelected && (
            <div>
              <label className="text-xs font-medium text-text-secondary mb-2 block">Strategie reklam wideo</label>
              <div className="flex flex-wrap gap-1.5">
                {videoStrategies.map(strategy => {
                  const isSelected = selectedStrategies.includes(strategy.key);
                  return (
                    <button
                      key={strategy.key}
                      onClick={() => toggleStrategy(strategy.key)}
                      className={`px-2.5 py-1 rounded-lg text-[11px] border transition-all ${
                        isSelected
                          ? 'bg-info/10 text-accent-info border-info/30'
                          : 'text-text-muted border-border hover:border-border-strong'
                      }`}
                    >
                      {strategy.label}
                    </button>
                  );
                })}
              </div>
              {selectedStrategies.length === 0 && (
                <p className="text-[10px] text-warning mt-1.5">Wybierz co najmniej jedną strategię dla reklam wideo.</p>
              )}
            </div>
          )}

          {/* Variant count */}
          <div>
            <label className="text-xs font-medium text-text-secondary mb-2 block">
              Warianty per typ: <span className="text-text-primary">{variantCount}</span>
            </label>
            <input
              type="range"
              min={1}
              max={5}
              value={variantCount}
              onChange={e => setVariantCount(Number(e.target.value))}
              className="w-full accent-accent-primary"
            />
            <div className="flex justify-between text-[10px] text-text-muted mt-1">
              <span>1</span><span>2</span><span>3</span><span>4</span><span>5</span>
            </div>
          </div>

          {/* Style presets */}
          <div>
            <label className="text-xs font-medium text-text-secondary mb-2 block">Style presets (opcjonalne)</label>
            <div className="space-y-1.5">
              {STYLE_PRESETS.map(preset => {
                const isSelected = selectedPresets.includes(preset.id);
                return (
                  <button
                    key={preset.id}
                    onClick={() => togglePreset(preset.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left text-xs border transition-all ${
                      isSelected
                        ? 'bg-accent-primary/10 text-accent-primary border-accent-primary/30'
                        : 'text-text-secondary border-border hover:border-border-strong'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                      isSelected ? 'bg-accent-primary/20 border-accent-primary/50' : 'border-border-strong'
                    }`}>
                      {isSelected && <span className="text-accent-primary text-[10px]">✓</span>}
                    </div>
                    <div>
                      <span className="text-text-primary font-medium">{preset.label}</span>
                      <span className="text-text-muted ml-2">{preset.desc}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-surface-hover/50 rounded-lg p-3 border border-border-subtle">
            <p className="text-xs text-text-secondary">
              Łącznie do wygenerowania: <span className="text-text-primary font-semibold">{totalJobs}</span> treści
            </p>
            {totalJobs > 10 && (
              <p className="text-[10px] text-warning mt-1">To może zająć trochę czasu i zużyć sporo tokenów AI.</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-border">
          <Button
            variant="primary"
            size="md"
            onClick={handleStart}
            loading={submitting}
            disabled={selectedTypes.length === 0 || submitting || (videoSelected && selectedStrategies.length === 0)}
            leftIcon={<Zap />}
          >
            Uruchom batch
          </Button>
          <Button variant="ghost" size="md" onClick={onClose}>
            Anuluj
          </Button>
        </div>
      </div>
    </div>
  );
}
